import React, { useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, CheckCircle2, Layers, Calendar, User } from "lucide-react";
import { FaGithub as Github } from "react-icons/fa";

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(3, 7, 18, 0.85);
  backdrop-filter: blur(12px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  width: 100%;
  max-width: 820px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid rgba(6, 182, 212, 0.4);
  border-radius: 24px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.8), 0 0 40px rgba(6, 182, 212, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(6, 182, 212, 0.4);
    border-radius: 999px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(3, 7, 18, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #f8fafc;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ef4444;
    border-color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalBanner = styled.div`
  width: 100%;
  height: 280px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.bgSoft};

  @media (max-width: 640px) {
    height: 190px;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BannerGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(3, 7, 18, 0.2) 0%,
    rgba(15, 23, 42, 0.95) 100%
  );
`;

const ModalBody = styled.div`
  padding: 28px 32px 36px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 640px) {
    padding: 20px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const ProjectTitle = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.3;

  @media (max-width: 640px) {
    font-size: 20px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #06b6d4;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: ${({ theme }) => theme.text_secondary};
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HighlightItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.5;

  svg {
    color: #10b981;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const TechTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TechTag = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 999px;
  background: ${({ theme }) => `${theme.primary}18`};
  color: ${({ theme }) => theme.primary};
  border: 1px solid ${({ theme }) => `${theme.primary}44`};
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  width: fit-content;
`;

const MemberAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #06b6d4;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const MemberRole = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.a`
  flex: 1;
  min-width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;

  ${({ primary, theme }) =>
    primary
      ? `
    background: linear-gradient(135deg, #06b6d4 0%, #0284c7 100%);
    color: #030712;
    box-shadow: 0 4px 18px rgba(6, 182, 212, 0.4);

    &:hover {
      background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(6, 182, 212, 0.55);
    }
  `
      : `
    background: ${theme.bgSoft};
    color: ${theme.text_primary};
    border: 1px solid ${theme.border};

    &:hover {
      background: ${theme.card_hover};
      border-color: rgba(6, 182, 212, 0.4);
      color: #06b6d4;
      transform: translateY(-2px);
    }
  `}
`;

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  if (!project) return null;

  const techList = project.technologies || project.tags || [];

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </CloseButton>

          <ModalBanner>
            <BannerImage
              src={project.image || project.fallbackImage || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"}
              alt={project.title}
            />
            <BannerGradient />
          </ModalBanner>

          <ModalBody>
            <HeaderRow>
              <div>
                <ProjectTitle>{project.title}</ProjectTitle>
                <MetaRow style={{ marginTop: "6px" }}>
                  <MetaItem>
                    <Layers size={14} color="#06b6d4" />
                    <span>{project.category}</span>
                  </MetaItem>
                  {project.date && (
                    <MetaItem>
                      <Calendar size={14} color="#8b5cf6" />
                      <span>{project.date}</span>
                    </MetaItem>
                  )}
                </MetaRow>
              </div>
            </HeaderRow>

            <Description>{project.description}</Description>

            {project.highlights && project.highlights.length > 0 && (
              <div>
                <SectionTitle>
                  <CheckCircle2 size={16} /> Key Architectural Highlights
                </SectionTitle>
                <HighlightsList style={{ marginTop: "12px" }}>
                  {project.highlights.map((item, index) => (
                    <HighlightItem key={index}>
                      <CheckCircle2 size={16} />
                      <span>{item}</span>
                    </HighlightItem>
                  ))}
                </HighlightsList>
              </div>
            )}

            <div>
              <SectionTitle>Technologies & Stack</SectionTitle>
              <TechTags style={{ marginTop: "10px" }}>
                {techList.map((tech, idx) => (
                  <TechTag key={idx}>{tech}</TechTag>
                ))}
              </TechTags>
            </div>

            {project.member && project.member.length > 0 && (
              <div>
                <SectionTitle>
                  <User size={16} /> Creator & Contributor
                </SectionTitle>
                <div style={{ marginTop: "10px" }}>
                  {project.member.map((m, idx) => (
                    <MemberCard key={idx}>
                      <MemberAvatar src={m.img} alt={m.name} />
                      <MemberInfo>
                        <MemberName>{m.name}</MemberName>
                        <MemberRole>{m.role || "Developer"}</MemberRole>
                      </MemberInfo>
                    </MemberCard>
                  ))}
                </div>
              </div>
            )}

            <ButtonRow>
              {project.webapp && (
                <ActionButton
                  primary={true}
                  href={project.webapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={16} />
                  Launch Live Web Application
                </ActionButton>
              )}
              {project.github && (
                <ActionButton
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github size={16} />
                  View GitHub Source Code
                </ActionButton>
              )}
            </ButtonRow>
          </ModalBody>
        </ModalContent>
      </Overlay>
    </AnimatePresence>
  );
};

export default ProjectModal;
